<template>
  <table class="sortable-table" :class="{ stackable }">
    <thead>
      <tr>
        <th
          v-for="column in columns"
          class="sortable-table-column"
          :class="{ active: !lockSort && column.key === sortByInternal }"
          :key="column.key"
        >
          <span
            class="sortable-table-column-button"
            :tabindex="lockSort ? null : '0'"
            :role="lockSort ? null : 'button'"
            :title="
              lockSort
                ? null
                : $t('components.sortable-table.sort-by-column-title', {
                    column: column.name.toLowerCase(),
                    direction: readableSortDirection,
                  })
            "
            @click="lockSort ? null : sortByKey(column.key)"
          >
            {{ column.name }}
            <FaIcon v-if="!lockSort" :icon="sortIcon" />
          </span>
        </th>
      </tr>
    </thead>
    <tbody>
      <tr
        class="sortable-table-row"
        v-for="row in data"
        :key="getRowKey(row)"
        ref="tableRows"
      >
        <slot v-bind:item="row">
          <td
            v-for="column in columns"
            :key="`${column.key}-${getRowKey(row)}`"
          >
            {{ getCellValue(row, column) }}
          </td>
        </slot>
      </tr>
    </tbody>
  </table>
</template>

<script>
import FaIcon from '@/components/ui/FaIcon'
import { getProperty } from '@/utils/object'

export const SortDirection = {
  ASCENDING: 'asc',
  DESCENDING: 'desc',
}

/**
 * Displays a table with data from the array passed to the data property.
 * Column headers, sort values and possibly cell content extraction is defined trough the columns property.
 * Row content can also be defined using the default slot and the `item` property it has access to.
 *
 * @author Thomas Grinderslev <lritgr@egmont.com>
 *
 * TODO: Actual sorting isn't implemented yet
 */
export default {
  name: 'SortableTable',
  components: { FaIcon },
  props: {
    data: {
      type: Array,
      required: true,
    },
    columns: {
      type: Array,
      required: true,
    },
    lockSort: {
      type: Boolean,
      default: false,
    },
    rowKey: {
      type: [String, Function],
      required: true,
    },
    sortBy: {
      type: String,
      required: false,
    },
    sortDirection: {
      type: String,
      default: SortDirection.ASCENDING,
      validator: sortDirection =>
        Object.values(SortDirection).includes(sortDirection),
    },
    stackable: { type: Boolean, default: false },
  },
  data() {
    return {
      sortByInternal: this.sortBy
        ? this.sortBy
        : getProperty(this.columns[0], 'key'),
      sortDirectionInternal: this.sortDirection,
    }
  },
  computed: {
    readableSortDirection() {
      return this.sortDirectionInternal === SortDirection.ASCENDING
        ? this.$t('components.sortable-table.direction-ascending')
        : this.$t('components.sortable-table.direction-descending')
    },
    sortIcon() {
      return this.sortDirectionInternal === SortDirection.ASCENDING
        ? 'angle-down'
        : 'angle-up'
    },
    sortedData() {
      return [...this.data].sort(
        (a, b) =>
          this.getCellValue(a, this.sortByInternal) -
          this.getCellValue(b, this.sortByInternal)
      )
    },
  },
  methods: {
    /**
     * Returns the key for the given row based on the `row-key` property.
     * @param {Object} rowData
     * @returns {string}
     */
    getRowKey(rowData) {
      if (typeof this.rowKey === 'string') {
        return getProperty(rowData, this.rowKey)
      }

      return this.rowKey(rowData)
    },

    /**
     * Returns the text content for a table cell based on the given row and column data.
     * @param {Object} rowData
     * @param {Object} column
     * @returns {string}
     */
    getCellValue(rowData, column) {
      switch (typeof column.value) {
        case 'string':
          return getProperty(rowData, column.value)
        case 'undefined':
          return ''
      }

      return column.value(rowData)
    },

    sortByKey(key) {
      if (this.sortByInternal === key) {
        if (this.sortDirectionInternal === SortDirection.ASCENDING) {
          this.sortDirectionInternal = SortDirection.DESCENDING
        } else {
          this.sortDirectionInternal = SortDirection.ASCENDING
        }
      } else {
        this.sortByInternal = key
      }
    },

    /**
     * Adds a data-label attribute to all TD's that are direct siblings of the TR elements to aid labeling on narrow screens.
     */
    updateLabels() {
      this.$refs.tableRows.forEach(tr => {
        tr.querySelectorAll(':scope > td').forEach((td, index) => {
          td.setAttribute(
            'data-label',
            getProperty(this.columns[index], 'name', '')
          )
        })
      })
    },
  },
  mounted() {
    this.updateLabels()
  },
  updated() {
    this.updateLabels()
  },
}
</script>

<style lang="scss" scoped>
$tableCellPadding: 0.5em 1em;

.sortable-table {
  --row-border-radius: var(--border-radius-medium);
  --theme-color: #{$orange};

  width: 100%;

  > thead {
    border-bottom: 2px solid var(--theme-color);
  }
}

tr:nth-child(even) {
  > td,
  > th {
    background: $grayLighter;

    &:first-child {
      border-radius: var(--row-border-radius) 0 0 var(--row-border-radius);
    }

    &:last-child {
      border-radius: 0 var(--row-border-radius) var(--row-border-radius) 0;
    }
  }
}
.sortable-table-column {
  font-size: 1em;

  &.active {
    font-weight: bold;
  }
}
.sortable-table-column-button {
  padding: $tableCellPadding;
}

td {
  padding: $tableCellPadding;
}

@media (max-width: 35em) {
  .sortable-table.stackable {
    display: block;
    width: auto;

    > thead {
      display: none;
    }

    > tbody,
    > tbody > tr,
    > tbody > tr > th,
    > tbody > tr > td {
      display: block;
      width: auto;
    }
    > tbody > tr > td {
      border: none;
      position: relative;
      padding-left: 30%;
      min-height: 1.7em;

      &:before {
        position: absolute;
        left: 0.6em;
        width: 25%;
        padding-right: 1em;
        display: block;
        content: attr(data-label);
        font-weight: bold;
        font-size: 0.8em;
        text-align: right;
        color: $gray;
      }
    }

    > tbody > tr {
      margin-bottom: 1em;

      > td,
      > th {
        &:first-child {
          padding-top: 1em;
          border-radius: var(--row-border-radius) var(--row-border-radius) 0 0;
        }

        &:last-child {
          padding-bottom: 1em;
          border-radius: 0 0 var(--row-border-radius) var(--row-border-radius);
        }
      }

      &:nth-child(even) {
        > td,
        > th {
          background: $grayLighter;
        }
      }

      &:nth-child(odd) {
        > td,
        > th {
          background: $offWhite;
        }
      }
    }
  }
}
</style>
